import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RosterBuilder from '../components/RosterBuilder';
import ScoringRulesEditor, { PRESETS, derivedPreset } from '../components/ScoringRulesEditor';
import {
  getUserDrafts,
  getDraftById,
  getYahooAuthUrl,
  getYahooTokenStatus,
  getYahooLeagues,
  importYahooRoster,
  getYahooScoringSettings,
  getYahooRosterPositions,
  getYahooLeagueTeams,
  importYahooDraft,
  getEspnLeague,
  importEspnDraft,
  getEspnCredentialStatus,
  getAggregatedRankings,
  createLeague,
  createLeagueFromDraft,
  getOpponents,
  createOpponent,
  linkOpponentToLeagueSlot,
  linkOpponentToLeagueSlotByExternalKey,
  unlinkOpponentFromLeagueSlotByExternalKey,
  updateLeagueRoster,
} from '../services/api';
import OpponentPicker from '../components/OpponentPicker';
import EspnCredentialModal from '../components/EspnCredentialModal';

const PRESET_LABELS = { standard: 'Standard', half_ppr: '0.5 PPR', ppr: 'PPR', custom: 'Custom' };

const DEFAULT_META = {
  leagueName: '',
  sport: 'nfl',
  seasonYear: new Date().getFullYear(),
  teamCount: 10,
  seasonType: 'REDRAFT',
};

const SEASON_TYPE_OPTIONS = [
  { key: 'REDRAFT', label: 'Redraft' },
  { key: 'DYNASTY', label: 'Dynasty' },
];

/**
 * Multi-step league creation flow.
 * Step 1: choose source (from scratch / from draft / from Yahoo)
 * Step 2: league metadata + source-specific picker (draft picker or Yahoo league picker)
 * Step 3: build / review roster
 * Step 4: confirm & save
 */
function LeagueCreateView() {
  const { isLoggedIn, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(1);
  const [source, setSource] = useState(null); // 'scratch' | 'draft' | 'yahoo'
  const [meta, setMeta] = useState({ ...DEFAULT_META });
  const [scoringRules, setScoringRules] = useState(PRESETS.half_ppr);
  const [rosterConfig, setRosterConfig] = useState(null);
  const [yahooScoringImported, setYahooScoringImported] = useState(false);
  const [espnScoringImported, setEspnScoringImported] = useState(false);
  const [roster, setRoster] = useState([]);
  const [sourceDraftId, setSourceDraftId] = useState(null);
  const [yahooLeagueKey, setYahooLeagueKey] = useState(null);

  // Data for pickers
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [selectedDraftId, setSelectedDraftId] = useState(null);
  const [yahooLeagues, setYahooLeagues] = useState([]);
  const [loadingYahooLeagues, setLoadingYahooLeagues] = useState(false);
  const [yahooConnected, setYahooConnected] = useState(false);
  // Whether this user already has a stored Yahoo token from a prior league — lets the UI offer
  // to reuse it instead of re-prompting for OAuth (mirrors the ESPN credential-reuse pattern).
  const [yahooAlreadyConnected, setYahooAlreadyConnected] = useState(false);
  const [yahooReconnecting, setYahooReconnecting] = useState(false);
  const [selectedYahooLeague, setSelectedYahooLeague] = useState(null);
  // NFL seasons are identified by the year they start (Sept–Feb).
  // In Jan–Aug the most recent season is still the previous calendar year.
  const currentNflSeason = new Date().getMonth() >= 8
    ? new Date().getFullYear()        // Sep–Dec: current year's season is underway
    : new Date().getFullYear() - 1;   // Jan–Aug: previous year's season was the last one
  const [yahooSeason, setYahooSeason] = useState(currentNflSeason);
  const [loadingYahooRoster, setLoadingYahooRoster] = useState(false);
  const [rankings, setRankings] = useState([]);
  const [loadingRankings, setLoadingRankings] = useState(false);

  const [yahooTeams, setYahooTeams] = useState([]);
  const [ownerSlotIndex, setOwnerSlotIndex] = useState(-1);
  const [selectedDraftTeams, setSelectedDraftTeams] = useState([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [opponents, setOpponents] = useState([]);
  // Map of slotIndex → opponentId (used for scratch/yahoo paths)
  const [opponentLinks, setOpponentLinks] = useState({});

  const [importDraftHistory, setImportDraftHistory] = useState(false);
  const [draftImportResult, setDraftImportResult] = useState(null); // { draftId }
  const [draftImportError, setDraftImportError] = useState(false);
  const [createdLeagueId, setCreatedLeagueId] = useState(null);

  // ESPN path — no OAuth redirect; credentials are cookies entered via a modal
  const [espnCredentialsSaved, setEspnCredentialsSaved] = useState(false);
  const [showEspnModal, setShowEspnModal] = useState(false);
  const [espnLeagueId, setEspnLeagueId] = useState('');
  const [espnSeasonYear, setEspnSeasonYear] = useState(currentNflSeason);
  const [espnTeams, setEspnTeams] = useState([]);
  const [loadingEspnLeague, setLoadingEspnLeague] = useState(false);

  // Opponent linking for the ESPN draft-import path, done AFTER league+draft creation
  // (real team names/roster only exist post-import) rather than in step 4 pre-creation.
  const [postImportTeams, setPostImportTeams] = useState([]);
  const [postImportOpponentLinks, setPostImportOpponentLinks] = useState({});

  function fetchYahooLeagues() {
    setLoadingYahooLeagues(true);
    setYahooLeagues([]);
    setError(null);
    getYahooLeagues()
      .then(data => setYahooLeagues(data))
      .catch(() => setError('Failed to load Yahoo leagues'))
      .finally(() => setLoadingYahooLeagues(false));
  }

  // Handle return from Yahoo OAuth
  useEffect(() => {
    const yahooParam = searchParams.get('yahoo');
    if (yahooParam === 'connected') {
      setSource('yahoo');
      setYahooConnected(true);
      // This path bypasses handleChooseSource (which normally seeds importDraftHistory),
      // since the OAuth redirect lands here directly rather than via the step-1 source
      // buttons -- set it explicitly so Yahoo's always-on draft import (see
      // handleChooseSource) also applies to this entry point.
      setImportDraftHistory(true);
      setStep(2);
      fetchYahooLeagues();
    } else if (yahooParam === 'error') {
      setSource('yahoo');
      setStep(2);
      setError('Yahoo authorization failed. Please try again.');
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load opponent list for the picker in step 4
  useEffect(() => {
    getOpponents().then(setOpponents).catch(() => {});
  }, []);

  // Load aggregated rankings when entering roster step
  useEffect(() => {
    if (step === 3 && rankings.length === 0) {
      setLoadingRankings(true);
      getAggregatedRankings()
        .then(data => setRankings(data))
        .catch(() => {}) // rankings are optional — search just won't return results
        .finally(() => setLoadingRankings(false));
    }
  }, [step, rankings.length]);

  if (authLoading) {
    return <div className='loading'><div className='spinner' /><p>Loading...</p></div>;
  }

  if (!isLoggedIn) {
    return (
      <div className='card' style={{ maxWidth: 480, margin: '48px auto', textAlign: 'center' }}>
        <h2>Add League</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>
          Sign in to create and manage your leagues.
        </p>
        <button className='btn btn-primary' onClick={() => navigate('/login?returnTo=/league/new')}>
          Sign in to continue
        </button>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Step 1 — Choose source
  // -------------------------------------------------------------------------

  function handleChooseSource(chosen) {
    setSource(chosen);
    setError(null);
    if (chosen === 'draft') {
      setLoadingDrafts(true);
      getUserDrafts()
        .then(data => setDrafts(data.filter(d => d.status === 'completed')))
        .catch(() => setError('Failed to load drafts'))
        .finally(() => setLoadingDrafts(false));
    }
    if (chosen === 'espn') {
      // Skip the "Connect ESPN Account" modal if this user already has stored credentials
      // from a prior league. Fail open (leave espnCredentialsSaved at its default false,
      // i.e. show the modal) on { exists: false } or any error from this check -- a hiccup
      // here should never block the source-selection flow.
      getEspnCredentialStatus()
        .then(({ exists }) => {
          if (exists) setEspnCredentialsSaved(true);
        })
        .catch(() => {});
    }
    if (chosen === 'yahoo' && !yahooConnected) {
      // Same reuse pattern as ESPN above: skip re-prompting for OAuth if this user already has
      // a stored Yahoo token from a prior league. Fail open on { exists: false } or any error --
      // the plain "Connect Yahoo Account" button is still shown either way.
      setYahooReconnecting(false);
      getYahooTokenStatus()
        .then(({ exists }) => {
          if (exists) setYahooAlreadyConnected(true);
        })
        .catch(() => {});
    }
    // ESPN has no separate roster-import endpoint (unlike Yahoo's importYahooRoster) --
    // draft-history import is the only way to populate a roster, so always do it rather
    // than make the user opt in via a checkbox. Yahoo now matches this unconditionally too
    // (previously opt-in via a checkbox, removed for consistency -- see the "Import draft
    // history" checkbox block that used to render below). Reset for other paths in case
    // the user picked ESPN/Yahoo, went back, and chose a different source.
    setImportDraftHistory(chosen === 'espn' || chosen === 'yahoo');
    setStep(2);
  }

  // -------------------------------------------------------------------------
  // Step 2 — Draft picker (from-draft path)
  // -------------------------------------------------------------------------

  async function handleSelectDraft(draft) {
    setSelectedDraftId(draft.draftId);
    setSourceDraftId(draft.draftId);
    setMeta(prev => ({
      ...prev,
      teamCount: draft.teamCount || prev.teamCount,
      seasonYear: draft.seasonYear || prev.seasonYear,
      leagueName: prev.leagueName || (draft.draftName ? draft.draftName + ' League' : prev.leagueName),
    }));
    // Seed scoring rules from the draft's scoring format
    const preset = draft.scoringFormat && PRESETS[draft.scoringFormat]
      ? draft.scoringFormat
      : 'half_ppr';
    setScoringRules(PRESETS[preset]);
    setYahooScoringImported(false);
    // Fetch full draft to get team slots with existing opponent links
    try {
      const fullDraft = await getDraftById(draft.draftId);
      const teams = fullDraft.teams || [];
      setSelectedDraftTeams(teams);
      // Pre-populate opponentLinks from existing opponent assignments
      const links = {};
      teams.forEach(t => {
        if (!t.isOwnerSlot && t.opponent) {
          links[t.slotIndex] = t.opponent.id;
        }
      });
      setOpponentLinks(links);
    } catch {
      setSelectedDraftTeams([]);
    }
  }

  // -------------------------------------------------------------------------
  // Step 2 — Yahoo league picker (yahoo path)
  // -------------------------------------------------------------------------

  async function handleConnectYahoo() {
    try {
      sessionStorage.setItem('yahooReturnTo', '/league/new');
      const { url } = await getYahooAuthUrl();
      window.location.href = url;
    } catch {
      setError('Failed to get Yahoo authorization URL');
    }
  }

  // User already has a stored Yahoo token (yahooAlreadyConnected) and chose to reuse it --
  // skip the OAuth redirect entirely and go straight to the league picker.
  function handleUseExistingYahoo() {
    setYahooConnected(true);
    fetchYahooLeagues();
  }

  function handleSelectYahooLeague(yahooLeague) {
    setSelectedYahooLeague(yahooLeague);
    setYahooLeagueKey(yahooLeague.yahooLeagueKey);
    setMeta(prev => ({
      ...prev,
      leagueName: prev.leagueName || yahooLeague.name || prev.leagueName,
      teamCount: yahooLeague.teamCount || prev.teamCount,
      seasonYear: yahooLeague.season ? Number(yahooLeague.season) : prev.seasonYear,
      seasonType: yahooLeague.seasonType || 'REDRAFT',
    }));
    setYahooScoringImported(false);
  }

  async function handleYahooPathNext() {
    if (!selectedYahooLeague) {
      setError('Please select a Yahoo league first');
      return;
    }
    setError(null);
    setLoadingYahooRoster(true);
    try {
      const [rosterResult, scoringResult, teamsResult, rosterConfigResult] = await Promise.allSettled([
        importYahooRoster(selectedYahooLeague.yahooLeagueKey),
        getYahooScoringSettings(selectedYahooLeague.yahooLeagueKey),
        getYahooLeagueTeams(selectedYahooLeague.yahooLeagueKey),
        getYahooRosterPositions(selectedYahooLeague.yahooLeagueKey),
      ]);

      if (rosterResult.status === 'rejected') {
        throw new Error('Failed to import Yahoo roster');
      }
      setRoster(rosterResult.value);

      if (scoringResult.status === 'fulfilled' && Object.keys(scoringResult.value).length > 0) {
        setScoringRules(scoringResult.value);
        setYahooScoringImported(true);
      }
      // Scoring failure: silently keep current scoringRules

      if (rosterConfigResult.status === 'fulfilled' && Object.keys(rosterConfigResult.value).length > 0) {
        setRosterConfig(rosterConfigResult.value);
      }
      // Roster-config failure: silently leave null -- backend defaults to a standard shape

      if (teamsResult.status === 'fulfilled') {
        setYahooTeams(teamsResult.value);
        setOwnerSlotIndex(teamsResult.value.findIndex(t => t.isUserTeam));
      }
      // Teams failure: silently keep yahooTeams as [] — step 4 falls back to generic labels

      // Yahoo's roster is already real at this point (unlike ESPN, whose roster only
      // becomes real after league+draft creation) -- skip the editable roster step
      // entirely, same as ESPN, since editing it would just get silently overwritten by
      // the real imported data. Fallback: an empty import (rosterResult.value.length === 0)
      // still lands on step 3 so a failed/partial import isn't a dead end.
      setStep(rosterResult.value.length > 0 ? 4 : 3);
    } catch {
      setError('Failed to import Yahoo roster');
    } finally {
      setLoadingYahooRoster(false);
    }
  }

  // -------------------------------------------------------------------------
  // Step 2 — ESPN league entry (espn path)
  // -------------------------------------------------------------------------

  function handleEspnCredentialsSaved() {
    setEspnCredentialsSaved(true);
    setShowEspnModal(false);
  }

  async function handleEspnPathNext() {
    if (!espnLeagueId.trim()) {
      setError('Please enter your ESPN league ID');
      return;
    }
    setError(null);
    setLoadingEspnLeague(true);
    try {
      const { league, teams, scoringRules: importedScoringRules } = await getEspnLeague(espnLeagueId.trim(), espnSeasonYear);
      setMeta(prev => ({
        ...prev,
        leagueName: prev.leagueName || league.name || prev.leagueName,
        teamCount: league.teamCount || prev.teamCount,
        seasonYear: league.seasonYear || prev.seasonYear,
        seasonType: league.seasonType || 'REDRAFT',
      }));
      if (league.rosterConfig && Object.keys(league.rosterConfig).length > 0) {
        setRosterConfig(league.rosterConfig);
      }
      // Real per-stat scoring rules, mirroring the Yahoo path's setScoringRules/
      // setYahooScoringImported below -- previously nothing ever set this for ESPN, so
      // every ESPN-imported league silently kept the frontend's half_ppr default.
      if (importedScoringRules && Object.keys(importedScoringRules).length > 0) {
        setScoringRules(importedScoringRules);
        setEspnScoringImported(true);
      }
      setEspnTeams(teams);
      setOwnerSlotIndex(teams.findIndex(t => t.isUserTeam));
      // With draft-history import, roster/team data only becomes real after the league
      // and draft are created (step 4's "Create League" action) -- skip the roster step
      // entirely rather than show a roster builder that's guaranteed empty at this point.
      setStep(importDraftHistory ? 4 : 3);
    } catch (err) {
      if (err.response?.data?.error?.code === 'ESPN_AUTH_EXPIRED') {
        setEspnCredentialsSaved(false);
        setError('Your ESPN session has expired — please re-enter your credentials.');
      } else {
        setError('Failed to import ESPN league');
      }
    } finally {
      setLoadingEspnLeague(false);
    }
  }

  // -------------------------------------------------------------------------
  // Step 2 → 3 transition for draft path
  // -------------------------------------------------------------------------

  async function handleDraftPathNext() {
    if (!selectedDraftId) {
      setError('Please select a draft first');
      return;
    }
    setError(null);
    // Roster will be loaded by createLeagueFromDraft on save; pre-populate here for preview
    const draft = drafts.find(d => d.draftId === selectedDraftId);
    if (draft && draft.roster) {
      setRoster(draft.roster.map(p => ({ ...p, acquisitionType: 'draft' })));
    }
    setStep(3);
  }

  // -------------------------------------------------------------------------
  // Opponent link helpers (used in step 4)
  // -------------------------------------------------------------------------

  function handleOpponentLink(slotIndex, opponentId) {
    setOpponentLinks(prev => ({ ...prev, [slotIndex]: opponentId }));
  }

  function handleOpponentUnlink(slotIndex) {
    setOpponentLinks(prev => {
      const next = { ...prev };
      delete next[slotIndex];
      return next;
    });
  }

  async function handleOpponentCreateAndLink(slotIndex, displayName) {
    try {
      const created = await createOpponent({ displayName });
      setOpponents(prev => [...prev, created]);
      setOpponentLinks(prev => ({ ...prev, [slotIndex]: created.id }));
    } catch {
      // silently ignore
    }
  }

  // -------------------------------------------------------------------------
  // Post-import opponent link helpers (ESPN draft-import path, step 4 after creation).
  // Keyed by externalTeamKey, not slotIndex -- league_teams and draft_teams slotIndex
  // numbering diverges for Yahoo/ESPN imports (league_teams keeps raw external-team-list
  // order, draft_teams reflects round-1 draft-pick order per the
  // draft-import-order-duplication fix), so a slotIndex-keyed link could silently attach
  // to the wrong team. externalTeamKey is the stable identity both tables agree on.
  // Only need to call the league-side by-key endpoint -- the backend cascades the same
  // link to the matching draft_teams slot itself (by externalTeamKey) now that the league
  // is linked to its source draft (via importEspnDraft's leagueId param ->
  // LeagueService.linkSourceDraft).
  // -------------------------------------------------------------------------

  function handlePostImportOpponentLink(externalTeamKey, opponentId) {
    setPostImportOpponentLinks(prev => ({ ...prev, [externalTeamKey]: opponentId }));
    linkOpponentToLeagueSlotByExternalKey(createdLeagueId, externalTeamKey, opponentId).catch(() => {});
  }

  function handlePostImportOpponentUnlink(externalTeamKey) {
    setPostImportOpponentLinks(prev => {
      const next = { ...prev };
      delete next[externalTeamKey];
      return next;
    });
    unlinkOpponentFromLeagueSlotByExternalKey(createdLeagueId, externalTeamKey).catch(() => {});
  }

  async function handlePostImportOpponentCreateAndLink(externalTeamKey, displayName) {
    try {
      const created = await createOpponent({ displayName });
      setOpponents(prev => [...prev, created]);
      handlePostImportOpponentLink(externalTeamKey, created.id);
    } catch {
      // silently ignore
    }
  }

  // -------------------------------------------------------------------------
  // Step 4 — Save
  // -------------------------------------------------------------------------

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      let result;
      if (source === 'draft' && sourceDraftId) {
        // Backend reads draft_teams directly — no teamNames needed
        result = await createLeagueFromDraft(sourceDraftId, {
          ...meta,
          scoringRules,
          yahooLeagueKey: null,
        });
      } else {
        // Build teamSlots for from-scratch, Yahoo, or ESPN paths
        const externalTeams = yahooTeams.length > 0 ? yahooTeams : espnTeams;
        const teamSlots = externalTeams.length > 0
          ? externalTeams.map((t, i) => ({
              slotIndex: i,
              teamName: t.teamName,
              isOwnerSlot: i === ownerSlotIndex,
              externalTeamKey: t.externalTeamKey || t.yahooTeamKey || null,
            }))
          : undefined;
        result = await createLeague({
          ...meta,
          scoringRules,
          rosterConfig,
          roster,
          teamSlots,
          yahooLeagueKey: yahooLeagueKey || null,
          espnLeagueId: source === 'espn' ? (espnLeagueId || null) : null,
          sourceDraftId: null,
        });
      }
      // Fire opponent links after league is created (best-effort, by slotIndex)
      await Promise.all(
        Object.entries(opponentLinks).map(([slotIndex, opponentId]) =>
          linkOpponentToLeagueSlot(result.leagueId, Number(slotIndex), opponentId).catch(() => {})
        )
      );

      if (importDraftHistory && yahooLeagueKey) {
        try {
          const opponentLinksByTeamKey = {};
          Object.entries(opponentLinks).forEach(([slotIndex, opponentId]) => {
            const team = yahooTeams[Number(slotIndex)];
            if (team?.yahooTeamKey) opponentLinksByTeamKey[team.yahooTeamKey] = opponentId;
          });
          const imported = await importYahooDraft(
            yahooLeagueKey,
            Object.keys(opponentLinksByTeamKey).length > 0 ? opponentLinksByTeamKey : undefined,
            result.leagueId
          );
          setDraftImportResult({ draftId: imported.draftId });
        } catch (err) {
          console.error('Failed to import Yahoo draft history', err);
          setDraftImportError(true);
        }
        setCreatedLeagueId(result.leagueId);
        setSaving(false);
        return;
      }

      if (importDraftHistory && source === 'espn' && espnLeagueId) {
        try {
          const opponentLinksByTeamKey = {};
          Object.entries(opponentLinks).forEach(([slotIndex, opponentId]) => {
            const team = espnTeams[Number(slotIndex)];
            if (team?.externalTeamKey) opponentLinksByTeamKey[team.externalTeamKey] = opponentId;
          });
          const imported = await importEspnDraft(
            espnLeagueId,
            espnSeasonYear,
            Object.keys(opponentLinksByTeamKey).length > 0 ? opponentLinksByTeamKey : undefined,
            result.leagueId
          );
          setDraftImportResult({ draftId: imported.draftId });
          setPostImportTeams(imported.teams || []);

          // ESPN has no roster-import endpoint (unlike Yahoo's importYahooRoster), so the
          // league was created with an empty roster above. Derive one from the owner's
          // picks in the draft we just imported, mirroring createLeagueFromDraft's logic.
          const ownerTeam = imported.teams?.find(t => t.isOwnerSlot);
          if (ownerTeam) {
            const ownerRoster = (imported.picks || [])
              .filter(p => p.slotIndex === ownerTeam.slotIndex)
              .map(p => ({ playerName: p.playerName, position: p.position, nflTeam: p.team, acquisitionType: 'draft' }));
            if (ownerRoster.length > 0) {
              await updateLeagueRoster(result.leagueId, ownerRoster).catch(() => {});
            }
          }
        } catch (err) {
          console.error('Failed to import ESPN draft history', err);
          if (err.response?.data?.error?.code === 'ESPN_AUTH_EXPIRED') {
            setEspnCredentialsSaved(false);
            setError('Your ESPN session has expired — please re-enter your credentials.');
          } else {
            setDraftImportError(true);
          }
        }
        setCreatedLeagueId(result.leagueId);
        setSaving(false);
        return;
      }

      navigate(`/league/${result.leagueId}`);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to create league');
      setSaving(false);
    }
  }

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const stepLabel = ['Choose source', 'League details', 'Roster', 'Confirm'][step - 1];
  const presetLabel = PRESET_LABELS[derivedPreset(scoringRules)] || 'Custom';

  return (
    <div className='card' style={{ maxWidth: 640, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h2 style={{ margin: 0 }}>Add League</h2>
        <button
          className='btn btn-secondary'
          onClick={() => {
            if (step === 4 && (
              (source === 'espn' && importDraftHistory) ||
              // Yahoo only skips step 3 when the import actually returned a roster
              // (see handleYahooPathNext's empty-roster fallback) -- roster.length is the
              // same signal used there, kept in sync so back-nav matches forward-nav.
              (source === 'yahoo' && importDraftHistory && roster.length > 0)
            )) {
              setStep(2); // step 3 (roster) is skipped entirely on this path
            } else if (step > 1) {
              setStep(step - 1);
            } else {
              navigate('/league');
            }
          }}
        >
          {step > 1 ? '← Back' : 'Cancel'}
        </button>
      </div>
      <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>Step {step} of 4 — {stepLabel}</p>

      {error && <div className='error-banner' style={{ marginBottom: 16 }}>{error}</div>}

      {/* ===== STEP 1 ===== */}
      {step === 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            {
              id: 'scratch',
              title: 'From scratch',
              desc: 'Manually enter your roster or import it from a CSV file.',
            },
            {
              id: 'draft',
              title: 'From a completed draft',
              desc: 'Pick a draft you ran here and carry your team into the season.',
            },
            {
              id: 'yahoo',
              title: 'Import from Yahoo',
              desc: 'Connect your Yahoo account and import your league roster automatically.',
            },
            {
              id: 'espn',
              title: 'Import from ESPN',
              desc: 'Connect your ESPN account and import your league roster automatically.',
            },
          ].map(opt => (
            <div
              key={opt.id}
              className='draft-list-item'
              role='button'
              tabIndex={0}
              onClick={() => handleChooseSource(opt.id)}
              onKeyDown={e => e.key === 'Enter' && handleChooseSource(opt.id)}
            >
              <div className='draft-list-item-header'>
                <span className='draft-list-item-name'>{opt.title}</span>
                <span style={{ color: 'var(--text-muted)', fontSize: '1rem' }}>→</span>
              </div>
              <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '0.85rem' }}>{opt.desc}</p>
            </div>
          ))}
        </div>
      )}

      {/* ===== STEP 2 ===== */}
      {step === 2 && (
        <div>
          {/* --- From draft: draft picker --- */}
          {source === 'draft' && (
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontWeight: 600, marginBottom: 8 }}>Select a completed draft</p>
              {loadingDrafts ? (
                <div className='loading'><div className='spinner' /></div>
              ) : drafts.length === 0 ? (
                <p style={{ color: 'var(--text-muted)' }}>No completed drafts found.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                  {drafts.map(d => (
                    <div
                      key={d.draftId}
                      className='draft-list-item'
                      role='button'
                      tabIndex={0}
                      onClick={() => handleSelectDraft(d)}
                      onKeyDown={e => e.key === 'Enter' && handleSelectDraft(d)}
                      style={{ outline: selectedDraftId === d.draftId ? '2px solid var(--primary, #2563eb)' : undefined }}
                    >
                      <div className='draft-list-item-header'>
                        <span className='draft-list-item-name'>{d.draftName || 'Untitled Draft'}</span>
                        {selectedDraftId === d.draftId && (
                          <span className='draft-status-badge active'>Selected</span>
                        )}
                      </div>
                      <div className='draft-list-item-meta'>
                        <span>{d.teamCount} teams</span>
                        <span>{d.scoringFormat}</span>
                        <span>{d.seasonYear || ''}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* --- From Yahoo: reuse-existing prompt, connect, or league picker --- */}
          {source === 'yahoo' && !yahooConnected && yahooAlreadyConnected && !yahooReconnecting && (
            <div style={{ textAlign: 'center', padding: '24px 0', marginBottom: 20 }}>
              <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
                You already have a Yahoo account connected.
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button className='btn btn-primary' onClick={handleUseExistingYahoo}>
                  Use existing account
                </button>
                <button className='btn btn-secondary' onClick={() => setYahooReconnecting(true)}>
                  Connect a different account
                </button>
              </div>
            </div>
          )}

          {source === 'yahoo' && !yahooConnected && (!yahooAlreadyConnected || yahooReconnecting) && (
            <div style={{ textAlign: 'center', padding: '24px 0', marginBottom: 20 }}>
              <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
                Connect your Yahoo account to import your league roster.
              </p>
              <button className='btn btn-primary' onClick={handleConnectYahoo}>
                Connect Yahoo Account
              </button>
            </div>
          )}

          {source === 'yahoo' && yahooConnected && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <p style={{ fontWeight: 600, margin: 0 }}>Select a Yahoo league</p>
                <div className='filter-group'>
                  {[currentNflSeason, currentNflSeason - 1, currentNflSeason - 2, currentNflSeason - 3].map(yr => (
                    <button
                      key={yr}
                      className={`filter-pill ${yahooSeason === yr ? 'active' : ''}`}
                      onClick={() => setYahooSeason(yr)}
                    >
                      {yr}
                    </button>
                  ))}
                </div>
              </div>
              {loadingYahooLeagues ? (
                <div className='loading'><div className='spinner' /></div>
              ) : yahooLeagues.filter(yl => String(yl.season) === String(yahooSeason)).length === 0 ? (
                <p style={{ color: 'var(--text-muted)' }}>
                  {yahooLeagues.length === 0
                    ? 'No Yahoo NFL leagues found.'
                    : `No leagues found for ${yahooSeason}.`}
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                  {yahooLeagues.filter(yl => String(yl.season) === String(yahooSeason)).map(yl => (
                    <div
                      key={yl.yahooLeagueKey}
                      className='draft-list-item'
                      role='button'
                      tabIndex={0}
                      onClick={() => handleSelectYahooLeague(yl)}
                      onKeyDown={e => e.key === 'Enter' && handleSelectYahooLeague(yl)}
                      style={{ outline: selectedYahooLeague?.yahooLeagueKey === yl.yahooLeagueKey ? '2px solid var(--primary, #2563eb)' : undefined }}
                    >
                      <div className='draft-list-item-header'>
                        <span className='draft-list-item-name'>{yl.name}</span>
                        {selectedYahooLeague?.yahooLeagueKey === yl.yahooLeagueKey && (
                          <span className='draft-status-badge active'>Selected</span>
                        )}
                      </div>
                      <div className='draft-list-item-meta'>
                        <span>{yl.teamCount} teams</span>
                        <span>{yl.season}</span>
                        <span>{yl.scoringType}</span>
                        {yl.seasonType === 'DYNASTY' && <span>Dynasty</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* --- From ESPN: connect or league entry --- */}
          {source === 'espn' && !espnCredentialsSaved && (
            <div style={{ textAlign: 'center', padding: '24px 0', marginBottom: 20 }}>
              <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
                Connect your ESPN account to import your league roster.
              </p>
              <button className='btn btn-primary' onClick={() => setShowEspnModal(true)}>
                Connect ESPN Account
              </button>
              {showEspnModal && (
                <EspnCredentialModal
                  onClose={() => setShowEspnModal(false)}
                  onSaved={handleEspnCredentialsSaved}
                />
              )}
            </div>
          )}

          {source === 'espn' && espnCredentialsSaved && (
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontWeight: 600, marginBottom: 8 }}>Enter your ESPN league</p>
              <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                <label style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 4 }}>ESPN league ID</span>
                  <input
                    className='search-input'
                    style={{ width: '100%' }}
                    placeholder='e.g. 123456'
                    value={espnLeagueId}
                    onChange={e => setEspnLeagueId(e.target.value)}
                  />
                </label>
                <label style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 4 }}>Season year</span>
                  <input
                    className='search-input'
                    type='number'
                    style={{ width: '100%' }}
                    value={espnSeasonYear}
                    onChange={e => setEspnSeasonYear(Number(e.target.value))}
                  />
                </label>
              </div>
              {espnLeagueId.trim() && (
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  Your draft history and roster will be imported automatically.
                </p>
              )}
            </div>
          )}

          {/* Metadata form (always shown in step 2 except Yahoo/ESPN pre-connect) */}
          {!(source === 'yahoo' && !yahooConnected) && !(source === 'espn' && !espnCredentialsSaved) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label>
                <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 4 }}>League name</span>
                <input
                  className='search-input'
                  style={{ width: '100%' }}
                  placeholder='My Fantasy League'
                  value={meta.leagueName}
                  onChange={e => setMeta(m => ({ ...m, leagueName: e.target.value }))}
                />
              </label>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Scoring</span>
                  <span className='filter-pill active' style={{ pointerEvents: 'none' }}>{presetLabel}</span>
                  {yahooScoringImported && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Imported from Yahoo</span>
                  )}
                  {espnScoringImported && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Imported from ESPN</span>
                  )}
                </div>
                <details>
                  <summary style={{ cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-muted)', userSelect: 'none' }}>
                    {source === 'yahoo' || source === 'espn' ? 'View scoring' : 'Customize scoring'}
                  </summary>
                  <div style={{ marginTop: 8 }}>
                    {/* Locked for the whole Yahoo/ESPN path, not just once yahooScoringImported/
                        espnScoringImported flip true -- step 2's metadata form (this block) is
                        visible as soon as a league is selected/connected, before the actual
                        import call runs, so gating only on the "imported" flags would leave a
                        brief window where the still-default rules are editable and any edit
                        gets silently discarded once the real import overwrites them moments
                        later. Locking on source alone closes that window entirely. */}
                    <ScoringRulesEditor
                      rules={scoringRules}
                      onChange={setScoringRules}
                      readOnly={source === 'yahoo' || source === 'espn'}
                    />
                  </div>
                </details>
              </div>

              <div>
                <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 4 }}>Season type</span>
                {(source === 'yahoo' || source === 'espn' || source === 'draft') ? (
                  // Import/draft flows: sourced from the platform/draft, not user-editable here.
                  <span className='filter-pill active' style={{ pointerEvents: 'none' }}>
                    {meta.seasonType === 'DYNASTY' ? 'Dynasty' : 'Redraft'}
                  </span>
                ) : (
                  <div className='filter-group'>
                    {SEASON_TYPE_OPTIONS.map(({ key, label }) => (
                      <button
                        type='button'
                        key={key}
                        className={`filter-pill ${meta.seasonType === key ? 'active' : ''}`}
                        onClick={() => setMeta(m => ({ ...m, seasonType: key }))}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 4 }}>Season year</span>
                  <input
                    className='search-input'
                    type='number'
                    style={{ width: '100%' }}
                    value={meta.seasonYear}
                    onChange={e => setMeta(m => ({ ...m, seasonYear: Number(e.target.value) }))}
                  />
                </label>
                <label style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 4 }}>Teams in league</span>
                  <input
                    className='search-input'
                    type='number'
                    style={{ width: '100%' }}
                    value={meta.teamCount}
                    onChange={e => setMeta(m => ({ ...m, teamCount: Number(e.target.value) }))}
                  />
                </label>
              </div>

              <button
                className='btn btn-primary'
                onClick={
                  source === 'draft' ? handleDraftPathNext :
                  source === 'yahoo' ? handleYahooPathNext :
                  source === 'espn' ? handleEspnPathNext :
                  () => { setError(null); setStep(3); }
                }
                disabled={
                  (source === 'draft' && !selectedDraftId) ||
                  (source === 'yahoo' && !selectedYahooLeague) ||
                  (source === 'espn' && !espnLeagueId.trim()) ||
                  loadingYahooRoster ||
                  loadingEspnLeague
                }
              >
                {loadingYahooRoster || loadingEspnLeague ? 'Importing…' : 'Next →'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ===== STEP 3 — Roster ===== */}
      {step === 3 && (
        <div>
          <p style={{ color: 'var(--text-muted)', marginBottom: 12 }}>
            {source === 'draft'
              ? 'Your draft picks are pre-loaded. You can add or remove players before saving.'
              : source === 'yahoo'
              ? 'Your Yahoo roster is pre-loaded. You can edit it before saving.'
              : source === 'espn'
              ? 'Add your players manually or import a CSV below. To import your roster automatically, go back and check "Import draft history".'
              : 'Add your players manually or import a CSV (Player Name, Position, NFL Team).'}
          </p>
          {loadingRankings && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Loading player database...</p>
          )}
          <RosterBuilder roster={roster} onChange={setRoster} rankings={rankings} />
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
            <button className='btn btn-primary' onClick={() => { setError(null); setStep(4); }}>
              Next →
            </button>
          </div>
        </div>
      )}

      {/* ===== STEP 4 — Confirm ===== */}
      {step === 4 && (
        <div>
          <table style={{ width: '100%', fontSize: '0.9rem', borderCollapse: 'collapse', marginBottom: 20 }}>
            <tbody>
              {[
                ['League name', meta.leagueName || '(untitled)'],
                ['Sport', 'NFL'],
                ['Scoring', presetLabel],
                ['Season', meta.seasonYear],
                ['Season type', meta.seasonType === 'DYNASTY' ? 'Dynasty' : 'Redraft'],
                ['Teams', meta.teamCount],
                ['Roster size', source === 'espn' && importDraftHistory && !createdLeagueId
                  ? 'Imported automatically'
                  : roster.length + ' players'],
                ['Source', source === 'draft' ? 'Draft import' : source === 'yahoo' ? 'Yahoo import' : source === 'espn' ? 'ESPN import' : 'Manual'],
              ].map(([label, value]) => (
                <tr key={label} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px', color: 'var(--text-muted)', width: 140 }}>{label}</td>
                  <td style={{ padding: '8px', fontWeight: 500 }}>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* On the ESPN draft-import path, team names/roster don't exist yet at this point
              -- they're only created when "Create League" below actually runs the import.
              Opponent linking for that path happens in the post-import section further down. */}
          {source === 'espn' && importDraftHistory && !createdLeagueId && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 20 }}>
              Your teams and roster will be imported when you create the league below.
              You'll be able to link opponents to your real teams right after.
            </p>
          )}

          {/* Opponent assignment (optional) */}
          {!(source === 'espn' && importDraftHistory) && (
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontWeight: 600, marginBottom: 8, fontSize: '0.9rem' }}>
              Manager Assignments <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span>
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '4px 8px', fontSize: '0.8rem', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>Team</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', fontSize: '0.8rem', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>Manager</th>
                </tr>
              </thead>
              <tbody>
                {source === 'draft'
                  ? (selectedDraftTeams.length > 0 ? selectedDraftTeams : Array.from({ length: meta.teamCount }, (_, i) => ({
                      id: i + 1, slotIndex: i, teamName: `Team ${i + 1}`, isOwnerSlot: false, opponent: null,
                    }))).map(t => {
                      const linkedId = opponentLinks[t.slotIndex];
                      const linkedOpponent = linkedId ? opponents.find(o => o.id === linkedId) || null : null;
                      return (
                        <tr key={t.slotIndex} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '6px 8px', fontSize: '0.875rem' }}>
                            {t.teamName}
                            {t.isOwnerSlot && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 4 }}>(You)</span>}
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            {t.isOwnerSlot ? (
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>—</span>
                            ) : (
                              <OpponentPicker
                                currentOpponent={linkedOpponent}
                                opponents={opponents}
                                onLink={id => handleOpponentLink(t.slotIndex, id)}
                                onUnlink={() => handleOpponentUnlink(t.slotIndex)}
                                onCreateAndLink={n => handleOpponentCreateAndLink(t.slotIndex, n)}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })
                  : source === 'yahoo' && yahooTeams.length > 0
                  ? yahooTeams.map((t, i) => {
                      const isOwner = i === ownerSlotIndex;
                      const linkedId = opponentLinks[i];
                      const linkedOpponent = linkedId ? opponents.find(o => o.id === linkedId) || null : null;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '6px 8px', fontSize: '0.875rem' }}>
                            {t.teamName}
                            {isOwner && (
                              <>
                                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 4 }}>(You)</span>
                                <button
                                  type='button'
                                  onClick={() => setOwnerSlotIndex(-1)}
                                  style={{ marginLeft: 4, fontSize: '0.7rem', color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: '1px 3px' }}
                                  title='Unmark as my team'
                                >
                                  ×
                                </button>
                              </>
                            )}
                            {!isOwner && (
                              <button
                                type='button'
                                onClick={() => {
                                  setOwnerSlotIndex(i);
                                  setOpponentLinks(prev => { const next = { ...prev }; delete next[i]; return next; });
                                }}
                                style={{ marginLeft: 6, fontSize: '0.7rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', padding: '1px 4px', textDecoration: 'underline' }}
                              >
                                This is me
                              </button>
                            )}
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            {isOwner ? (
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>—</span>
                            ) : (
                              <OpponentPicker
                                currentOpponent={linkedOpponent}
                                opponents={opponents}
                                onLink={id => handleOpponentLink(i, id)}
                                onUnlink={() => handleOpponentUnlink(i)}
                                onCreateAndLink={n => handleOpponentCreateAndLink(i, n)}
                                suggestedName={t.teamName}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })
                  : source === 'espn' && espnTeams.length > 0
                  ? espnTeams.map((t, i) => {
                      const isOwner = i === ownerSlotIndex;
                      const linkedId = opponentLinks[i];
                      const linkedOpponent = linkedId ? opponents.find(o => o.id === linkedId) || null : null;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '6px 8px', fontSize: '0.875rem' }}>
                            {t.teamName}
                            {isOwner && (
                              <>
                                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 4 }}>(You)</span>
                                <button
                                  type='button'
                                  onClick={() => setOwnerSlotIndex(-1)}
                                  style={{ marginLeft: 4, fontSize: '0.7rem', color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: '1px 3px' }}
                                  title='Unmark as my team'
                                >
                                  ×
                                </button>
                              </>
                            )}
                            {!isOwner && (
                              <button
                                type='button'
                                onClick={() => {
                                  setOwnerSlotIndex(i);
                                  setOpponentLinks(prev => { const next = { ...prev }; delete next[i]; return next; });
                                }}
                                style={{ marginLeft: 6, fontSize: '0.7rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', padding: '1px 4px', textDecoration: 'underline' }}
                              >
                                This is me
                              </button>
                            )}
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            {isOwner ? (
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>—</span>
                            ) : (
                              <OpponentPicker
                                currentOpponent={linkedOpponent}
                                opponents={opponents}
                                onLink={id => handleOpponentLink(i, id)}
                                onUnlink={() => handleOpponentUnlink(i)}
                                onCreateAndLink={n => handleOpponentCreateAndLink(i, n)}
                                suggestedName={t.teamName}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })
                  : Array.from({ length: meta.teamCount }, (_, i) => {
                      const linkedId = opponentLinks[i];
                      const linkedOpponent = linkedId ? opponents.find(o => o.id === linkedId) || null : null;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '6px 8px', fontSize: '0.875rem' }}>{`Team ${i + 1}`}</td>
                          <td style={{ padding: '6px 8px' }}>
                            <OpponentPicker
                              currentOpponent={linkedOpponent}
                              opponents={opponents}
                              onLink={id => handleOpponentLink(i, id)}
                              onUnlink={() => handleOpponentUnlink(i)}
                              onCreateAndLink={name => handleOpponentCreateAndLink(i, name)}
                            />
                          </td>
                        </tr>
                      );
                    })
                }
              </tbody>
            </table>
          </div>
          )}

          {/* Post-import opponent linking (ESPN draft-import path only) -- real team
              names/roster now exist since the league + draft were just created. */}
          {source === 'espn' && importDraftHistory && createdLeagueId && draftImportResult && postImportTeams.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontWeight: 600, marginBottom: 8, fontSize: '0.9rem' }}>
                Manager Assignments <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span>
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 8px', fontSize: '0.8rem', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>Team</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', fontSize: '0.8rem', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>Manager</th>
                  </tr>
                </thead>
                <tbody>
                  {postImportTeams.map(t => {
                    const linkedId = postImportOpponentLinks[t.externalTeamKey];
                    const linkedOpponent = linkedId ? opponents.find(o => o.id === linkedId) || null : null;
                    return (
                      <tr key={t.slotIndex} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 8px', fontSize: '0.875rem' }}>
                          {t.teamName}
                          {t.isOwnerSlot && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 4 }}>(You)</span>}
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          {t.isOwnerSlot ? (
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>—</span>
                          ) : (
                            <OpponentPicker
                              currentOpponent={linkedOpponent}
                              opponents={opponents}
                              onLink={id => handlePostImportOpponentLink(t.externalTeamKey, id)}
                              onUnlink={() => handlePostImportOpponentUnlink(t.externalTeamKey)}
                              onCreateAndLink={n => handlePostImportOpponentCreateAndLink(t.externalTeamKey, n)}
                              suggestedName={t.teamName}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {createdLeagueId ? (
            <>
              {draftImportResult && (
                <div className='success-banner'>
                  Draft imported — <Link to={`/draft/${draftImportResult.draftId}`}>View Draft</Link>
                </div>
              )}
              {draftImportError && (
                <div className='warning-banner'>
                  Draft import failed — you can retry from your profile
                </div>
              )}
              <button
                className='btn btn-primary'
                style={{ width: '100%' }}
                onClick={() => navigate(`/league/${createdLeagueId}`)}
              >
                Continue to League
              </button>
            </>
          ) : (
            <button
              className='btn btn-primary'
              style={{ width: '100%' }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving
                ? 'Saving…'
                : (source === 'espn' || source === 'yahoo') && importDraftHistory
                ? 'Create League & Import Draft'
                : 'Create League'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default LeagueCreateView;
